import { Link, getRouteApi } from '@tanstack/react-router'
import { CalendarClock, Trophy, Users } from 'lucide-react'

import { PageIntro } from '@/components/page-intro'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { ContestStatusBadge } from '@/components/ui/status'
import { useContests } from '@/features/contest/api'
import { formatDate, formatNumber, formatRupiah, formatRelative } from '@/lib/format'

const route = getRouteApi('/public/sayembara')

export function ContestsPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()

  const { data, isLoading } = useContests({ status: search.status ?? 'open', page: search.page })
  const contests = data?.contests ?? []
  const meta = data?.meta

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
      <PageIntro
        accent="sayembara"
        eyebrow="Sayembara"
        icon={<Trophy className="size-3.5" />}
        title="Sayembara"
        description="Kirim karya, pemenang dipilih penyelenggara. Hadiah sudah terkunci di rekening penampungan sebelum sayembara tayang."
      />

      <div className="mt-6 flex flex-wrap gap-2">
        {[
          { value: 'open', label: 'Menerima karya' },
          { value: 'judging', label: 'Tahap penilaian' },
          { value: 'awarded', label: 'Sudah ada pemenang' },
        ].map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => navigate({ search: (previous) => ({ ...previous, status: option.value, page: 1 }) })}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${
              (search.status ?? 'open') === option.value
                ? 'border-brand bg-brand-soft text-brand-ink'
                : 'border-line text-muted hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <p className="mt-6 border-b border-line pb-4 text-sm text-muted" aria-live="polite">
        {isLoading ? 'Memuat sayembara…' : `${formatNumber(meta?.total ?? 0)} sayembara`}
      </p>

      {isLoading ? (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-64 rounded-2xl" />
          ))}
        </div>
      ) : contests.length === 0 ? (
        <Card className="mt-6">
          <EmptyState illustration="box"
            icon={<Trophy className="size-6" />}
            title="Belum ada sayembara"
            description="Sayembara baru akan muncul di sini. Anda juga dapat membuka sayembara sendiri."
            action={
              <Link to="/app/sayembara">
                <Button variant="outline">Buka sayembara</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {contests.map((contest) => (
            <Link
              key={contest.id}
              to="/sayembara/$contestId"
              params={{ contestId: contest.id }}
              className="card flex flex-col p-5 transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-lifted)]"
            >
              <div className="flex flex-wrap items-center gap-2">
                <ContestStatusBadge status={contest.status} />
                <Badge tone="brand">{contest.category}</Badge>
              </div>

              <h2 className="clamp-2 mt-3 font-bold leading-snug text-ink">{contest.title}</h2>
              <p className="clamp-3 mt-2 text-sm text-muted">{contest.description}</p>

              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-subtle">
                <span className="inline-flex items-center gap-1">
                  <Users className="size-3.5" />
                  {contest.entry_count} karya
                </span>
                {contest.closes_at ? (
                  <span className="inline-flex items-center gap-1">
                    <CalendarClock className="size-3.5" />
                    ditutup {formatDate(contest.closes_at)}
                  </span>
                ) : null}
                <span>dibuka {formatRelative(contest.created_at)}</span>
              </div>

              <div className="mt-auto border-t border-line pt-3">
                <p className="text-xs text-subtle">Hadiah</p>
                <p className="text-lg font-extrabold tracking-tight text-ink">
                  {formatRupiah(contest.prize_amount)}
                </p>
                <p className="text-xs text-subtle">
                  Pemenang menerima {formatRupiah(contest.net_to_winner)} setelah komisi platform
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
